import { useTranslation } from "react-i18next";
import { SafeText } from "@/shared/components/SafeText";
import { INVITATIONS_LOCATIONS_INLINE } from "./constants";
import type { InvitationLocation } from "./types";

interface InvitationLocationsProps {
  readonly invitationId: string;
  readonly locations: readonly InvitationLocation[];
}

function LocationItem({ location }: { readonly location: InvitationLocation }) {
  const { t } = useTranslation();
  return (
    <li className="break-words">
      <SafeText>{location.name}</SafeText>{" "}
      <span className="text-xs text-muted-foreground">
        ({t(`invitations.kind.${location.kind}`)})
      </span>
    </li>
  );
}

/**
 * Locations bound to an invitation: the first few inline, the rest behind a
 * "+N" disclosure (native `<details>`: keyboard and screen-reader friendly,
 * no popover to position). An unbound invitation is valid and says so.
 */
export function InvitationLocations({ invitationId, locations }: InvitationLocationsProps) {
  const { t } = useTranslation();
  if (locations.length === 0) {
    return (
      <span
        data-testid={`invitation-no-locations-${invitationId}`}
        className="text-muted-foreground"
      >
        {t("invitations.locations.none")}
      </span>
    );
  }
  const inline = locations.slice(0, INVITATIONS_LOCATIONS_INLINE);
  const rest = locations.slice(INVITATIONS_LOCATIONS_INLINE);
  return (
    <div data-testid={`invitation-locations-${invitationId}`} className="space-y-1">
      <ul className="space-y-0.5">
        {inline.map((l) => (
          <LocationItem key={`${l.kind}:${l.id}`} location={l} />
        ))}
      </ul>
      {rest.length > 0 && (
        <details data-testid={`invitation-locations-more-${invitationId}`}>
          <summary className="cursor-pointer text-xs font-medium text-primary">
            {t("invitations.locations.more", { count: rest.length })}
            <span className="sr-only">
              {" "}
              {t("invitations.locations.showMore", { count: rest.length })}
            </span>
          </summary>
          <ul className="mt-1 space-y-0.5">
            {rest.map((l) => (
              <LocationItem key={`${l.kind}:${l.id}`} location={l} />
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
